import { IFrame, Frame, IFrameBounds } from './IFrame';
import { IEventBus } from '../events/IEventBus';
import { DiagramType } from '../tools/interfaces/ITool';

export interface IFrameManagerOptions {
  allowNestedFrames?: boolean;
  maxNestingDepth?: number;
  autoActivateOnHover?: boolean;
}

export class FrameManager {
  private frames: Map<string, IFrame> = new Map();
  private activeFrame: IFrame | null = null;
  private hoveredFrame: IFrame | null = null;
  private eventBus: IEventBus;
  private options: Required<IFrameManagerOptions>;
  
  constructor(eventBus: IEventBus, options?: IFrameManagerOptions) {
    this.eventBus = eventBus;
    this.options = {
      allowNestedFrames: options?.allowNestedFrames ?? true,
      maxNestingDepth: options?.maxNestingDepth ?? 5,
      autoActivateOnHover: options?.autoActivateOnHover ?? false
    };
  }
  
  // Frame management
  addFrame(frame: IFrame): void {
    if (this.frames.has(frame.id)) {
      throw new Error(`Frame with id "${frame.id}" already exists`);
    }
    
    // Validate nesting depth if adding as child
    if (frame.parentFrame) {
      const depth = this.getFrameDepth(frame.parentFrame);
      if (depth >= this.options.maxNestingDepth) {
        throw new Error(`Maximum nesting depth (${this.options.maxNestingDepth}) exceeded`);
      }
      
      const parentFrame = this.frames.get(frame.parentFrame);
      if (parentFrame) {
        parentFrame.addChildFrame(frame.id);
      }
    }
    
    this.frames.set(frame.id, frame);
    this.eventBus.emit('frame:added', frame);
  }
  
  removeFrame(frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame) return;
    
    // Remove from parent if exists
    if (frame.parentFrame) {
      const parentFrame = this.frames.get(frame.parentFrame);
      parentFrame?.removeChildFrame(frameId);
    }
    
    // Handle child frames
    if (frame.childFrames) {
      frame.childFrames.forEach(childId => {
        const childFrame = this.frames.get(childId);
        if (childFrame) {
          childFrame.setParentFrame(undefined);
        }
      });
    }
    
    // Clear active/hovered if necessary
    if (this.activeFrame?.id === frameId) {
      this.activeFrame = null;
    }
    if (this.hoveredFrame?.id === frameId) {
      this.hoveredFrame = null;
    }
    
    this.frames.delete(frameId);
    this.eventBus.emit('frame:removed', frame);
  }
  
  getFrame(frameId: string): IFrame | undefined {
    return this.frames.get(frameId);
  }
  
  getAllFrames(): IFrame[] {
    return Array.from(this.frames.values());
  }
  
  getFramesOfType(type: DiagramType): IFrame[] {
    return Array.from(this.frames.values()).filter(frame => frame.type === type);
  }
  
  // Frame selection and activation
  setActiveFrame(frameId: string | null): void {
    const newFrame = frameId ? this.frames.get(frameId) || null : null;
    
    if (this.activeFrame?.id === newFrame?.id) return;
    
    const previousFrame = this.activeFrame;
    this.activeFrame = newFrame;
    
    this.eventBus.emit('frame:activated', {
      current: newFrame,
      previous: previousFrame
    });
  }
  
  getActiveFrame(): IFrame | null {
    return this.activeFrame;
  }
  
  // Spatial queries
  getFrameAtPoint(point: { x: number; y: number }, includeNested = true): IFrame | null {
    const frames = this.getFramesAtPoint(point, includeNested);
    return frames.length > 0 ? frames[0] : null;
  }
  
  getFramesAtPoint(point: { x: number; y: number }, includeNested = true): IFrame[] {
    // Get all frames that contain the point
    const containingFrames = Array.from(this.frames.values())
      .filter(frame => frame.visible !== false && frame.containsPoint(point.x, point.y));
    
    if (!includeNested) {
      return containingFrames;
    }
    
    // Sort by nesting depth (deepest first) and area (smallest first)
    containingFrames.sort((a, b) => {
      const depthA = this.getFrameDepth(a.id);
      const depthB = this.getFrameDepth(b.id);
      
      if (depthA !== depthB) {
        return depthB - depthA; // Deeper frames first
      }
      
      const areaA = a.bounds.width * a.bounds.height;
      const areaB = b.bounds.width * b.bounds.height;
      
      return areaA - areaB; // Smaller frames first
    });
    
    return containingFrames;
  }
  
  getFramesInBounds(bounds: IFrameBounds): IFrame[] {
    return Array.from(this.frames.values()).filter(frame => {
      if (frame.visible === false) return false;
      
      return !(frame.bounds.x + frame.bounds.width < bounds.x ||
               frame.bounds.x > bounds.x + bounds.width ||
               frame.bounds.y + frame.bounds.height < bounds.y ||
               frame.bounds.y > bounds.y + bounds.height);
    });
  }
  
  // Frame hierarchy
  getFrameDepth(frameId: string): number {
    const frame = this.frames.get(frameId);
    if (!frame) return 0;
    
    let depth = 0;
    let current = frame;
    
    while (current.parentFrame) {
      depth++;
      current = this.frames.get(current.parentFrame)!;
      if (!current) break;
      
      // Prevent infinite loops
      if (depth > 100) {
        console.warn('Possible circular frame hierarchy detected');
        break;
      }
    }
    
    return depth;
  }
  
  getRootFrames(): IFrame[] {
    return Array.from(this.frames.values()).filter(frame => !frame.parentFrame);
  }
  
  getChildFrames(frameId: string): IFrame[] {
    const frame = this.frames.get(frameId);
    if (!frame || !frame.childFrames) return [];
    
    return Array.from(frame.childFrames)
      .map(childId => this.frames.get(childId))
      .filter(Boolean) as IFrame[];
  }
  
  getFrameHierarchy(frameId: string): IFrame[] {
    const result: IFrame[] = [];
    let current = this.frames.get(frameId);
    
    while (current) {
      result.unshift(current);
      current = current.parentFrame ? this.frames.get(current.parentFrame) : undefined;
    }
    
    return result;
  }
  
  // Frame operations
  moveFrame(frameId: string, deltaX: number, deltaY: number): void {
    const frame = this.frames.get(frameId);
    if (!frame || frame.locked || !frame.bounds) return;
    
    const newBounds = {
      x: frame.bounds.x + deltaX,
      y: frame.bounds.y + deltaY
    };
    
    frame.setBounds(newBounds);
    
    // Move child frames if any
    if (frame.childFrames) {
      frame.childFrames.forEach(childId => {
        this.moveFrame(childId, deltaX, deltaY);
      });
    }
    
    this.eventBus.emit('frame:moved', { frame, deltaX, deltaY });
  }
  
  resizeFrame(frameId: string, bounds: Partial<IFrameBounds>): void {
    const frame = this.frames.get(frameId);
    if (!frame || frame.locked) return;
    
    const oldBounds = frame.getBounds();
    frame.setBounds(bounds);
    
    this.eventBus.emit('frame:resized', { frame, oldBounds, newBounds: frame.getBounds() });
  }
  
  // Shape/Connector management
  addShapeToFrame(shapeId: string, frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame) return;
    
    // Remove from other frames first
    this.removeShapeFromAllFrames(shapeId);
    
    frame.addShape(shapeId);
    this.eventBus.emit('frame:shapeAdded', { frame, shapeId });
  }
  
  removeShapeFromFrame(shapeId: string, frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame) return;
    
    frame.removeShape(shapeId);
    this.eventBus.emit('frame:shapeRemoved', { frame, shapeId });
  }
  
  removeShapeFromAllFrames(shapeId: string): void {
    this.frames.forEach(frame => {
      if (frame.containsShape(shapeId)) {
        frame.removeShape(shapeId);
      }
    });
  }
  
  getFrameForShape(shapeId: string): IFrame | null {
    for (const frame of this.frames.values()) {
      if (frame.containsShape(shapeId)) {
        return frame;
      }
    }
    return null;
  }
  
  addConnectorToFrame(connectorId: string, frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame) return;
    
    // Remove from other frames first
    this.removeConnectorFromAllFrames(connectorId);
    
    frame.addConnector(connectorId);
    this.eventBus.emit('frame:connectorAdded', { frame, connectorId });
  }
  
  removeConnectorFromFrame(connectorId: string, frameId: string): void {
    const frame = this.frames.get(frameId);
    if (!frame) return;
    
    frame.removeConnector(connectorId);
    this.eventBus.emit('frame:connectorRemoved', { frame, connectorId });
  }
  
  removeConnectorFromAllFrames(connectorId: string): void {
    this.frames.forEach(frame => {
      if (frame.containsConnector(connectorId)) {
        frame.removeConnector(connectorId);
      }
    });
  }
  
  getFrameForConnector(connectorId: string): IFrame | null {
    for (const frame of this.frames.values()) {
      if (frame.containsConnector(connectorId)) {
        return frame;
      }
    }
    return null;
  }
  
  // Mouse interaction
  handleMouseMove(point: { x: number; y: number }): void {
    const frame = this.getFrameAtPoint(point, true);
    
    if (frame?.id !== this.hoveredFrame?.id) {
      const previousFrame = this.hoveredFrame;
      this.hoveredFrame = frame;
      
      this.eventBus.emit('frame:hovered', {
        current: frame,
        previous: previousFrame
      });
      
      if (this.options.autoActivateOnHover && frame) {
        this.setActiveFrame(frame.id);
      }
    }
  }
  
  // Serialization
  toJSON(): any {
    return {
      frames: Array.from(this.frames.values()).map(frame => frame.toJSON()),
      activeFrameId: this.activeFrame?.id,
      options: this.options
    };
  }
  
  fromJSON(json: any): void {
    this.clear();
    
    if (json.frames) {
      json.frames.forEach((frameData: any) => {
        const frame = Frame.fromJSON(frameData);
        this.frames.set(frame.id, frame);
      });
    }
    
    if (json.activeFrameId) {
      this.setActiveFrame(json.activeFrameId);
    }
    
    if (json.options) {
      this.options = { ...this.options, ...json.options };
    }
  }
  
  clear(): void {
    this.frames.clear();
    this.activeFrame = null;
    this.hoveredFrame = null;
    this.eventBus.emit('frame:cleared');
  }
  
  dispose(): void {
    this.clear();
  }
}